/**
 * ReportsPage — Dedrone-style Summary + Alert List report (Phase 3 item 19).
 *
 * Layout mirrors Dedrone's Summary Report page:
 *   Header      — title, date range, advanced filter, export menu
 *   KPI strip   — five tiles from `summary.kpis`
 *   Row 1       — [Alerts by Date (7 cols)] [Alerts by Hour/Day (5 cols)]
 *   Row 2       — [Top Drones] [Top Serials] [Top Protocols]
 *   Row 3       — Latest Alerts table (clickable → AlertDetailDrawer)
 *
 * "Show as alert list" from the export menu swaps the body for a full-width
 * paginated alert list view. The header + filters stay in place.
 *
 * All data comes from `/reports/summary` (one request). The paginated list
 * view queries `/reports/alerts` independently. Drawer is `/reports/alerts/:id`.
 */
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FunnelIcon,
  ExclamationTriangleIcon,
  RocketLaunchIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import { reportsApi, type ReportFilterInput } from '../services/reportsApi';
import { geofencesApi, sensorsApi } from '../services/api';
import { SkeletonCard, SkeletonChart } from '../components/common/Skeleton';

import DateRangeSelector, { DEFAULT_RANGE, type DateRange } from '../components/reports/DateRangeSelector';
import ReportFilterPopover from '../components/reports/ReportFilterPopover';
import ExportMenu from '../components/reports/ExportMenu';
import SummaryKpisCard from '../components/reports/SummaryKpisCard';
import AlertsByDateChart from '../components/reports/AlertsByDateChart';
import AlertsByHourDowHeatmap from '../components/reports/AlertsByHourDowHeatmap';
import RankedListCard from '../components/reports/RankedListCard';
import LatestAlertsTable from '../components/reports/LatestAlertsTable';
import AlertDetailDrawer from '../components/reports/AlertDetailDrawer';
import AlertListView from '../components/reports/AlertListView';
import AISummaryCard from '../components/reports/AISummaryCard';

type ViewMode = 'summary' | 'alerts';

interface Geofence { id: string; name: string; }
interface Sensor { id: string; name: string; }

function countActiveFilters(f: ReportFilterInput): number {
  let n = 0;
  for (const [k, v] of Object.entries(f)) {
    if (k === 'from' || k === 'to') continue;
    if (v !== undefined && v !== null && v !== '') n++;
  }
  return n;
}

export default function ReportsPage() {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [mode, setMode] = useState<ViewMode>('summary');
  const [filters, setFilters] = useState<ReportFilterInput>({});
  const [filterOpen, setFilterOpen] = useState(false);

  // The drawer state lives in the URL (?alert=<id>) so that the browser
  // back button closes the drawer instead of leaving /reports entirely.
  // Each open pushes a new history entry; close pops back to the URL
  // before the drawer was opened.
  const [searchParams, setSearchParams] = useSearchParams();
  const drawerId = searchParams.get('alert');
  const setDrawerId = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set('alert', id);
          else next.delete('alert');
          return next;
        },
        // Open uses push (so back closes the drawer); close also uses push
        // to keep behavior symmetric — the alternative (replace on close)
        // would surprise users who expect "forward" to reopen the drawer.
        { replace: false },
      );
    },
    [setSearchParams],
  );

  // Compose the filter payload the server expects. Memoized so React Query's
  // key stays referentially stable when filters don't change.
  const apiFilters: ReportFilterInput = useMemo(
    () => ({
      ...filters,
      from: range.from.toISOString(),
      to: range.to.toISOString(),
    }),
    [filters, range],
  );

  const summaryQuery = useQuery({
    queryKey: ['reports', 'summary', apiFilters],
    queryFn: () => reportsApi.getSummary(apiFilters),
    enabled: mode === 'summary',
    staleTime: 60 * 1000,
  });

  // Dropdown options for the advanced-filter panel. Light queries — we don't
  // paginate sensors/geofences; a deployment with hundreds can be revisited.
  const geofencesQuery = useQuery({
    queryKey: ['reports', 'geofences-list'],
    queryFn: async () => {
      const res = await geofencesApi.list();
      return (res.data as Geofence[]) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const sensorsQuery = useQuery({
    queryKey: ['reports', 'sensors-list'],
    queryFn: async () => {
      const res = await sensorsApi.list();
      return (res.data as Sensor[]) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const activeFilterCount = countActiveFilters(filters);

  return (
    <div className="min-h-screen bg-navy-900 p-6 grid-overlay">
      {/* ---- Header ---- */}
      <div className="mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="mt-1 text-sm text-gray-500">
            {range.label} · {summaryQuery.data
              ? `${summaryQuery.data.kpis.totalAlerts.toLocaleString()} detections`
              : 'Loading…'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <DateRangeSelector value={range} onChange={setRange} />
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <FunnelIcon className="h-4 w-4" />
            Advanced Filter
            {activeFilterCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded bg-accent text-navy-900 text-[10px] font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
          <ExportMenu
            mode={mode}
            onShowAsList={() => setMode('alerts')}
            onShowAsSummary={() => setMode('summary')}
          />
        </div>
      </div>

      {/* Active-filter chip strip */}
      {activeFilterCount > 0 && (
        <div className="mb-4 flex items-center gap-2 text-xs">
          <span className="text-gray-500">Active filters:</span>
          {Object.entries(filters).map(([k, v]) => {
            if (k === 'from' || k === 'to' || v === undefined || v === null || v === '') return null;
            return (
              <span
                key={k}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-navy-800 border border-navy-700 text-gray-300"
              >
                <span className="text-gray-500">{k}:</span>
                <span>{String(v)}</span>
              </span>
            );
          })}
          <button
            type="button"
            onClick={() => setFilters({})}
            className="text-accent hover:text-accent-light"
          >
            Clear all
          </button>
        </div>
      )}

      {/* ---- Error banner ---- */}
      {summaryQuery.isError && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 text-sm">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>
            Failed to load report: {(summaryQuery.error as Error)?.message || 'Unknown error'}
          </span>
        </div>
      )}

      {/* ---- Body ---- */}
      {mode === 'summary' && (
        <div className="space-y-6">
          {/* AI summary — sits above the KPI strip so the qualitative
              read frames the quantitative metrics below. */}
          <AISummaryCard filters={apiFilters} />

          {/* KPI strip */}
          {summaryQuery.isLoading || !summaryQuery.data ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : (
            <SummaryKpisCard kpis={summaryQuery.data.kpis} />
          )}

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7">
              {summaryQuery.isLoading || !summaryQuery.data ? (
                <SkeletonChart />
              ) : (
                <AlertsByDateChart data={summaryQuery.data.alertsByDate} />
              )}
            </div>
            <div className="lg:col-span-5">
              {summaryQuery.isLoading || !summaryQuery.data ? (
                <SkeletonChart />
              ) : (
                <AlertsByHourDowHeatmap matrix={summaryQuery.data.alertsByHourDow} />
              )}
            </div>
          </div>

          {/* Top-N row. "Top Protocols" was removed per Phase 5B — operators
              cared about drones and serials, not the wire protocol. The
              backend still computes it (cheap; might come back as a debug
              widget) but the UI layout is now two-up. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {summaryQuery.isLoading || !summaryQuery.data ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : (
              <>
                <RankedListCard
                  title="Top Drones"
                  icon={RocketLaunchIcon}
                  rows={summaryQuery.data.topDrones.map((d) => ({
                    label: d.model || 'Unknown',
                    sublabel: d.manufacturer ?? undefined,
                    count: d.count,
                    repeatCount: d.repeatCount,
                  }))}
                />
                <RankedListCard
                  title="Top Serials"
                  icon={IdentificationIcon}
                  barColor="bg-sky-400/70"
                  rows={summaryQuery.data.topSerials.map((s) => ({
                    label: s.serialNumber,
                    count: s.count,
                    repeatCount: s.repeatCount,
                  }))}
                />
              </>
            )}
          </div>

          {/* Latest alerts */}
          {summaryQuery.isLoading || !summaryQuery.data ? (
            <SkeletonChart />
          ) : (
            <LatestAlertsTable
              rows={summaryQuery.data.latestAlerts}
              onRowClick={setDrawerId}
            />
          )}
        </div>
      )}

      {mode === 'alerts' && (
        <AlertListView filters={apiFilters} onRowClick={setDrawerId} />
      )}

      {/* ---- Overlays ---- */}
      <ReportFilterPopover
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={filters}
        onApply={setFilters}
        geofences={geofencesQuery.data}
        sensors={sensorsQuery.data}
      />

      <AlertDetailDrawer
        alertId={drawerId}
        open={drawerId !== null}
        onClose={() => setDrawerId(null)}
      />
    </div>
  );
}
